import { Button, Card, CardActions, CardContent, CardHeader } from '@material-ui/core';
import { CircularProgress } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import ErrorIcon from '@material-ui/icons/Error';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { messages } from './Dashboard.translations';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1
    },
    title: {
      color: theme.palette.primary.main
    }
  })
);

export interface DashboardItemResult {
  result: string;
  isResolved: boolean;
}
export interface Props {
  onLoad: () => Promise<DashboardItemResult>;
  resolveLink: string;
  headerText: string;
  bodyText: string;
}

const DashboardItem = (props: Props) => {
  const classes = useStyles();
  const {onLoad, resolveLink, headerText, bodyText} = props;
  const [value, setValue] = useState('0');
  const [isResolved, setIsResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const {formatMessage} = useIntl();
  const resolveText = formatMessage(messages['dashboard.buttons.resolve']);
  const noActionText = formatMessage(messages['dashboard.buttons.noAction']);
  const errorText = formatMessage(messages['dashboard.errorService']);

  useEffect(() => {
    onLoad().then((response: DashboardItemResult) => {
      setValue(response.result);
      setIsResolved(response.isResolved);
      setIsLoading(false);
      setIsError(false);
    }).catch(() => {
      setValue('-');
      setIsError(true);
      setIsResolved(true);
    });
  }, [setValue, onLoad, isError]);

  const ItemCardActions = () => {
    if (isError) {
      return (
        <CardActions>
          <ErrorIcon color={'secondary'} /> <Typography color={'secondary'} variant="body2">{errorText}</Typography>
        </CardActions>
      );
    }
    if (!isResolved) {
      return (
      <CardActions>
        <Button component={Link} color={'primary'} to={resolveLink}>{resolveText}</Button>
      </CardActions>
      );
    }
    return (
      <CardActions>
        <CheckIcon color={'primary'} /> <Typography color={'primary'} variant="body2">{noActionText}</Typography>
      </CardActions>
    );
  };

  const ItemCardContent = () => {
    if (isError) {
      return (
        <>
         <Typography variant="h2" align="center" gutterBottom={true}>
              -
          </Typography>
        </>
      );
    }
    if (isLoading) {
      return (
        <>
          <Typography variant="h2" align="center" gutterBottom={true}>
            <CircularProgress data-testid="dashboard-item-loading" />
          </Typography>
        </>
      );
    } else {
      return (
        <>
          <Typography variant="h2" align="center" gutterBottom={true}>
            <span data-testid="dashboard-item-value">{value}</span>
          </Typography>
          <Typography variant="body2">
            {bodyText}
          </Typography>
        </>
      );
    }
  };

  return (
    <Card className={classes.root} variant="outlined">
      <CardHeader classes={{ subheader: classes.title }} subheader={headerText} />
      <CardContent>
        <ItemCardContent />
      </CardContent>
      <ItemCardActions />
  </Card>
  );
};

export default DashboardItem;
